import { useCallback, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { useDispatch, useSelector } from "react-redux";

import {
  captureEvent,
  extractErrorProperties,
} from "context/posthog/captureEvent";
import { POSTHOG_EVENTS, POSTHOG_FLOWS } from "context/posthog/events";
import { useTranslatedToasts } from "hooks/toasts/useTranslatedToasts";
// eslint-disable-next-line no-restricted-imports
import { useLoading } from "modules/common/hooks/useLoading";

import { AUTH_TOASTS } from "../constants/AuthToasts";
import { ERRORS } from "../constants/Errors";
import { useSCAContext } from "../context/SCAContext";
import { getSCATranslationKey } from "../helpers/getSCATranslationKey";
import {
  SCA_LOGIN_ERROR_REASONS,
  ScaLoginError,
} from "../models/ScaLoginError";
import { SCA_ERRORS } from "../services/sca";
import {
  loginSCA,
  selectAccessToken,
  selectSCASubjectId,
} from "../store/auth.slice";
import { useCurrentUser } from "./useCurrentUser";
import { useDisableSCASetupFeatureFlag } from "./useDisableSCASetupFeatureFlag";
import { useIsCurrentUserVoltEmployee } from "./useIsCurrentUserVoltEmployee";
import { useSCALogin } from "./useSCALogin";
import { useSCASetup } from "./useSCASetup";

export function useSCA() {
  const { t } = useTranslation(["auth", "common"]);
  const { currentUser } = useCurrentUser();
  const { sca } = useSCAContext();
  const dispatch = useDispatch();
  const [error, setError] = useState<string | null>(null);
  const { isLoading, withLoading } = useLoading();
  const { initializeSCASetup, finalizeSCA, finalizeSCASetup } = useSCASetup();
  const { authenticateSCALogin } = useSCALogin();

  const subjectIdFromToken = useSelector(selectSCASubjectId);
  const subjectId =
    currentUser?.scaAuthentication?.subjectId ?? subjectIdFromToken;
  const hasSCA = Boolean(currentUser?.scaAuthentication?.active);
  const isVoltEmployee = useIsCurrentUserVoltEmployee();
  const isSCASetupDisabled = useDisableSCASetupFeatureFlag();
  const token = useSelector(selectAccessToken);
  const controller = useRef(new AbortController());
  const {
    showScaConfigurationSuccessToast,
    showScaAuthenticateFailureToast,
    showScaConfigurationFailureToast,
    showScaLoginFailureToast,
    showScaLoginRateLimitedToast,
  } = useTranslatedToasts(AUTH_TOASTS, "auth");

  const setupSCA = withLoading(async () => {
    cleanupError();

    if (isSCASetupDisabled) {
      return;
    }

    if (!currentUser?.email || !currentUser?.id) {
      return;
    }

    try {
      const initializeResult = await initializeSCASetup();

      if (initializeResult.data === undefined) {
        captureEvent(POSTHOG_EVENTS.scaSetupInitializeFailed, {
          flow: POSTHOG_FLOWS.scaSetup,
          ...extractErrorProperties(initializeResult.error),
        });

        throw new Error(ERRORS.SCA_INITIALIZATION);
      }

      let credential;

      try {
        credential = await sca?.createPasskey(initializeResult.data);
      } catch (passkeyError) {
        captureEvent(POSTHOG_EVENTS.scaSetupCreatePasskeyFailed, {
          flow: POSTHOG_FLOWS.scaSetup,
          ...extractErrorProperties(passkeyError),
        });

        throw passkeyError;
      }

      if (!credential) {
        captureEvent(POSTHOG_EVENTS.scaSetupCreatePasskeyFailed, {
          flow: POSTHOG_FLOWS.scaSetup,
          errorMessage: "missing_credential",
        });

        throw new Error(ERRORS.SCA_CREATION);
      }

      const finalizeResult = await finalizeSCASetup({
        id: currentUser.id,
        credential,
      });

      if (!("data" in finalizeResult)) {
        captureEvent(POSTHOG_EVENTS.scaSetupFinalizeFailed, {
          flow: POSTHOG_FLOWS.scaSetup,
          ...extractErrorProperties(finalizeResult.error),
        });

        throw new Error(ERRORS.SCA_FINALIZATION);
      }

      captureEvent(POSTHOG_EVENTS.scaSetupSucceeded, {
        flow: POSTHOG_FLOWS.scaSetup,
      });

      showScaConfigurationSuccessToast();
    } catch (error) {
      captureEvent(POSTHOG_EVENTS.scaSetupFailed, {
        flow: POSTHOG_FLOWS.scaSetup,
        ...extractErrorProperties(error),
      });

      showScaConfigurationFailureToast(
        t(getSCATranslationKey(error.message ?? "default"), { ns: "common" })
      );
    }
  });

  const authenticateSCAAction = useCallback(async (): Promise<
    string | undefined
  > => {
    cleanupError();

    if (!subjectId || !sca) {
      return;
    }

    try {
      const signal = controller.current.signal;
      const credential = await sca.getPasskey(signal);

      const { id, type, rawId, response } = credential;
      const scaToken = await sca.authenticate({
        id,
        type,
        rawId,
        response,
      });

      captureEvent(POSTHOG_EVENTS.scaAuthenticateSucceeded, {
        flow: POSTHOG_FLOWS.scaAuthenticate,
      });

      return scaToken;
    } catch (error) {
      captureEvent(POSTHOG_EVENTS.scaAuthenticateFailed, {
        flow: POSTHOG_FLOWS.scaAuthenticate,
        ...extractErrorProperties(error),
      });

      showScaAuthenticateFailureToast(
        t(getSCATranslationKey(error.message ?? "default"), { ns: "common" })
      );
    }
  }, [sca, showScaAuthenticateFailureToast, subjectId, t]);

  const login = useMemo(
    () =>
      withLoading(async () => {
        cleanupError();

        if (!subjectId || !token) {
          return;
        }

        try {
          const scaToken = await authenticateSCALogin({
            bearerToken: token,
            signal: controller.current.signal,
          });

          const result = await finalizeSCA({
            scaToken,
            bearerToken: token,
          });

          if (result.data === undefined) {
            throw new Error(ERRORS.SCA_FINALIZATION);
          }

          dispatch(loginSCA(result.data));

          captureEvent(POSTHOG_EVENTS.scaLoginSucceeded, {
            flow: POSTHOG_FLOWS.scaLogin,
          });
        } catch (error) {
          const errorProperties = extractErrorProperties(error);

          if (error instanceof ScaLoginError) {
            if (error.reason === SCA_LOGIN_ERROR_REASONS.CANCELLED) {
              captureEvent(POSTHOG_EVENTS.scaSigningCancelled, {
                flow: POSTHOG_FLOWS.scaLogin,
                ...errorProperties,
              });
              // The WebAuthn API failed. Usually in this case the user cancelled the WebAuthn dialog.
              setError(SCA_ERRORS.CANCEL);

              return;
            }

            if (error.reason === SCA_LOGIN_ERROR_REASONS.FORBIDDEN) {
              captureEvent(POSTHOG_EVENTS.scaLoginFailed, {
                flow: POSTHOG_FLOWS.scaLogin,
                reason: "unauthorized",
                ...errorProperties,
              });
              // The user needs to login to perform this action
              setError(SCA_ERRORS.UNAUTHORIZED);

              return;
            }

            if (error.reason === SCA_LOGIN_ERROR_REASONS.RATE_LIMITED) {
              captureEvent(POSTHOG_EVENTS.scaLoginFailed, {
                flow: POSTHOG_FLOWS.scaLogin,
                reason: SCA_LOGIN_ERROR_REASONS.RATE_LIMITED,
                ...errorProperties,
              });
              showScaLoginRateLimitedToast();

              return;
            }
          }

          captureEvent(POSTHOG_EVENTS.scaLoginFailed, {
            flow: POSTHOG_FLOWS.scaLogin,
            ...errorProperties,
          });

          showScaLoginFailureToast(
            t(getSCATranslationKey(error.message ?? "default"), {
              ns: "common",
            })
          );
        }
      }),
    [
      authenticateSCALogin,
      dispatch,
      finalizeSCA,
      showScaLoginFailureToast,
      showScaLoginRateLimitedToast,
      subjectId,
      t,
      token,
      withLoading,
    ]
  );

  const cancel = useCallback(() => {
    controller.current.abort();
  }, [controller]);

  const cleanupError = () => setError(null);

  const authenticateWithLoading = withLoading(authenticateSCAAction);

  return {
    shouldShowCard: !isVoltEmployee,
    isSCASetupDisabled,
    setupSCA,
    hasSCA,
    login,
    authenticateSCAAction: authenticateWithLoading,
    error,
    isLoading,
    cancel,
    cleanupError,
  };
}
